/**
 * The JSON shape of a block document (tiptap / ProseMirror JSON) and helpers that need no registry.
 * Owner: doc-engine (wave 1), page-editor (wave 2).
 */

export type DocumentMark = { type: string; attrs?: Record<string, unknown> };

export type DocumentNode = {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: DocumentMark[];
  content?: DocumentNode[];
};

/** A new block document: one empty paragraph. */
export const emptyDocument = (): DocumentNode => ({ type: 'doc', content: [{ type: 'paragraph' }] });

/** Links allowed in documents: http(s), mailto, tel and same-origin paths. Never `javascript:`. */
export function safeDocumentUrl(url: string): boolean {
  if ([...url].some((char) => char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127)) return false;
  return /^(https?:\/\/|mailto:|tel:)/i.test(url) || /^\/(?!\/|\\)[^\\]*$/.test(url);
}

/** Plain text of a document as used by mail, tasks and search (unchanged legacy behaviour). */
export function documentText(doc: DocumentNode): string {
  if (doc.type === 'text') return doc.text ?? '';
  if (doc.type === 'hardBreak') return '\n';
  const text = (doc.content ?? []).map(documentText).join('');
  return (
    text + (['paragraph', 'heading', 'codeBlock', 'listItem', 'taskItem'].includes(doc.type) ? '\n' : '')
  );
}

/** Depth-first visit of every node with its path of child indexes. */
export function walkDocument(
  node: DocumentNode,
  visit: (node: DocumentNode, path: number[], parent: DocumentNode | null) => void | 'skip',
  path: number[] = [],
  parent: DocumentNode | null = null,
): void {
  if (visit(node, path, parent) === 'skip') return;
  (node.content ?? []).forEach((child, i) => {
    if (child && typeof child === 'object') walkDocument(child, visit, [...path, i], node);
  });
}

/** A paragraph holding plain text (lines become hard breaks). */
export function paragraph(text: string): DocumentNode {
  const lines = text.split('\n');
  const content: DocumentNode[] = [];
  lines.forEach((line, i) => {
    if (i > 0) content.push({ type: 'hardBreak' });
    if (line) content.push({ type: 'text', text: line });
  });
  return content.length ? { type: 'paragraph', content } : { type: 'paragraph' };
}
